<script setup lang="ts">
import { VersionTypeDto } from "mws-finance-ts-sdk";
import { computed, ref, watchEffect } from "vue";
import { useToast } from "vue-toastification";

import BaseButton from "@/common/components/BaseButton.vue";
import CardVue from "@/common/components/card/CardVue.vue";
import PageWrapper from "@/common/components/PageWrapper.vue";
import PercentageInput from "@/common/components/PercentageInput.vue";
import { getErrorMessage } from "@/common/utils/getErrorMessage";
import useSelectedPlatform from "@/platforms/public/composables/useSelectedPlatform";
import {
  type DiscountRow,
  mapInput,
  transformDiscountToFormValue,
} from "@/upsell/utils";

import {
  useGetDiscountsQuery,
  usePostDiscountsMutation,
} from "../../api/queries";

definePage({
  meta: {
    auth: ["Upsell.Access"],
  },
});

const { platformKey } = useSelectedPlatform();
const toast = useToast();

const { mutateAsync: updateDiscountsAsync } = usePostDiscountsMutation();
const { data: discounts } = useGetDiscountsQuery();

const formData = ref<DiscountRow[]>([]);
const percentageColumns = ref<Array<VersionTypeDto>>([VersionTypeDto.Control]);

watchEffect(() => {
  const discountData = transformDiscountToFormValue(discounts.value);
  if (discountData.length) {
    // Populate the form with the initial discount data
    formData.value = discountData;

    // Dynamically determine percentageColumns based on discountData
    const baseKeys = ["from", "to"];
    const firstRow = discountData[0];

    if (firstRow) {
      percentageColumns.value = Object.keys(firstRow)
        .filter((key) => !baseKeys.includes(key))
        .map((key) => key as VersionTypeDto);
    }
  }
});

const addPercentageColumn = () => {
  if (!percentageColumns.value.includes(VersionTypeDto.A)) {
    percentageColumns.value.push(VersionTypeDto.A);

    return (formData.value = formData.value.map((row) => ({
      ...row,
      [VersionTypeDto.A]: "",
    })));
  }

  percentageColumns.value.push(VersionTypeDto.B);

  return (formData.value = formData.value.map((row) => ({
    ...row,
    [VersionTypeDto.B]: "",
  })));
};

const removePercentageColumn = () => {
  const last = percentageColumns.value[percentageColumns.value.length - 1];

  if (!last) return;

  percentageColumns.value.pop();
  formData.value = formData.value.map((row) => {
    const newRow = { ...row };
    delete newRow[last];

    return newRow;
  });
};

const isRemoveButtonEnabled = (col: string) => {
  const percentageColumnsLastIndex = percentageColumns.value.length - 1;

  return (
    col === percentageColumns.value[percentageColumnsLastIndex] &&
    percentageColumnsLastIndex !== 0
  );
};

const overlappingPriceRangeIndexes = computed(() => {
  const indexed = formData.value.map((row, index) => ({
    index,
    from: Number(row.from),
    to: Number(row.to),
  }));

  const sorted = [...indexed].sort((a, b) => a.from - b.from);

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (current!.to >= next!.from) {
      return [current?.index, next?.index];
    }
  }

  return [];
});

const updateDiscounts = async () => {
  const mappedInput = mapInput(
    formData.value,
    percentageColumns.value,
    platformKey.value,
  );

  try {
    await updateDiscountsAsync({ createOrUpdateDiscountRequest: mappedInput });
    toast.success("Discounts updated successfully");
  } catch (error) {
    toast.error(getErrorMessage(error)[0]);
  }
};
</script>

<template>
  <PageWrapper
    title="Upsell Discounts"
    :breadcrumb-links="[
      {
        label: 'Upsell Discounts',
        to: '#',
      },
    ]"
  >
    <CardVue>
      <BaseButton
        variant="secondary"
        label="Add Percentage Variant"
        class="mb-8"
        :disabled="percentageColumns.length === 3"
        data-test="add-percentage-variant-button"
        @click="addPercentageColumn"
      />
      <FormKitForm
        form-class="overflow-scroll"
        :submit-label="
          overlappingPriceRangeIndexes.length
            ? 'Fix errors before submitting'
            : 'Submit'
        "
        :submit-attrs="{
          disabled: !!overlappingPriceRangeIndexes.length,
          'data-test': 'discounts-submit',
        }"
        @submit="updateDiscounts"
      >
        <FormKit
          v-model="formData"
          type="repeater"
          label="Discount Group"
          item-class="!mb-4"
          inner-class="!w-fit"
          :up-control="false"
          :down-control="false"
          data-test="discount-form"
        >
          <template #default="{ index }">
            <div class="flex gap-4">
              <div class="flex flex-row gap-2">
                <FormKit
                  type="currency"
                  currency="EUR"
                  min-decimals="2"
                  label="From"
                  name="from"
                  data-test="discount-from"
                />
                <FormKit
                  type="currency"
                  currency="EUR"
                  min-decimals="2"
                  label="To"
                  name="to"
                  data-test="discount-to"
                />
              </div>

              <!-- Dynamic Percentage Columns -->
              <div class="flex flex-row gap-2">
                <div
                  v-for="col in percentageColumns"
                  :key="col"
                  class="flex flex-col items-start"
                  data-test="percentage-columns"
                >
                  <PercentageInput
                    :label="`Percentage version: ${col}`"
                    :name="col"
                    data-test="discount-percentage"
                  />
                  <button
                    v-if="isRemoveButtonEnabled(col)"
                    type="button"
                    class="text-sm text-red-500 hover:underline mt-1"
                    data-test="remove-percentage-variant"
                    @click="removePercentageColumn"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
            <p
              v-if="overlappingPriceRangeIndexes.includes(index)"
              class="text-red-500 text-xs"
            >
              This price range is overlapping with another dicount group
            </p>
          </template>
        </FormKit>
      </FormKitForm>
    </CardVue>
  </PageWrapper>
</template>
